import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Paper,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Input } from "@mui/material";
import bgImage from '../assets/bg.svg';
import Footer from '../components/Footer';
import ReCaptcha from "../components/ReCaptcha";

const LoginPage = () => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { loginWithGoogle, isAuthenticated, user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const tenantId = searchParams.get('tenantId');
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const handleCaptchaVerify = (status) => {
    setCaptchaVerified(status);
  };

  useEffect(() => {
    const checkUserAndRedirect = async () => {
      if (isAuthenticated && user) {
        // Check if user exists in Firestore
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (!userDoc.exists()) {
          // Not authorized, redirect to registration page with tenantId
          navigate(`/visitor-qr-registration?tenantId=${tenantId || ''}`);
          return;
        }
        // User exists, proceed as normal
        if (isSuperAdmin() || user.role === 'superadmin') {
          navigate('/superadmin-dashboard', { replace: true });
        } else if (user.role === 'appadmin') {
          navigate('/tenant-admin-dashboard', { replace: true });
        } else if (user.role === 'facilityadmin') {
          navigate('/facility-dashboard', { replace: true });
        } else if (user.role === 'host' || user.role === 'tenant') {
          navigate('/host-dashboard', { replace: true });
        } else if (user.role === 'appoperator') {
          navigate('/app-operator-dashboard', { replace: true });
        } else {
          navigate('/visitor-dashboard', { replace: true });
        }
      }
    };
    checkUserAndRedirect();
  }, [isAuthenticated, user, navigate, isSuperAdmin, tenantId]);

  const handleGoogleLogin = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setError('');

    try {
      await loginWithGoogle(false);
    } catch (error) {
      console.error('Google Sign-In Error:', error);
      if (error.message && error.message.includes('No account found')) {
        // Redirect to registration page with tenantId if present
        navigate(`/visitor-qr-registration?tenantId=${tenantId || ''}`);
        return;
      }
      setError(error.message || 'Failed to log in');
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {

    nav: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      height: "64px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      p: "0 5vw",
      background: "rgba(254,252,232,.94)",
      backdropFilter: "blur(14px)",
      borderBottom: "2px solid #f0d060"
    },

    logo: {
      fontSize: "1.2rem",
      fontWeight: 800,
      color: "#1c1400",
      display: "flex",
      alignItems: "center"
    },

    unbounded: {
      fontFamily: "'Unbounded', sans-serif"
    },

    loginWrap: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      minHeight: "calc(100vh - 70px)",

      "@media (max-width: 900px)": {
        gridTemplateColumns: "1fr"
      }
    }

  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#fff" }}>

      {/* NAVBAR */}
      <Box sx={styles.nav}>
        <Typography sx={{ ...styles.logo, ...styles.unbounded }}>
          AALAY
          <Box component="span" sx={{
            display: "inline-block",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: "#f59e0b",
            ml: 0.5,
            verticalAlign: "middle"
          }} />
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          {/* <Button sx={{
            p: "8px 18px",
            borderRadius: "6px",
            border: "2px solid #f0d60",
            background: "transparent",
            color: "#3d2e00",
            fontSize: "0.8rem",
            fontWeight: 500,
            "&:hover": {
              borderColor: "#f59e0b",
              color: "#ca8a04"
            }

          }}
            onClick={() => navigate("/login")}>
            Sign in
          </Button> */}
          <Button sx={{
            p: "9px 22px",
            borderRadius: "6px",
            backgroundColor: "#1c1400",
            border: "2px solid #1c1400",
            color: "#fde047",
            fontSize: "0.75rem",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            fontFamily: "'Unbounded', sans-serif",
            "&:hover": {
              backgroundColor: "#f59e0b",
              borderColor: "#f59e0b",
              color: "#1c1400"
            }
          }}
            onClick={() => navigate("/")}>
            BACK TO HOME ←
          </Button>
        </Box>
      </Box>

      {/* MAIN LOGIN */}
      <Box sx={styles.loginWrap}>

        {/* LEFT PANEL */}
        <Box sx={{
          backgroundColor: "#fde047",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          p: { xs: "40px 7vw", md: "80px 7vw" },
          position: "relative",
          overflow: "hidden",
          borderRight: "2px solid #f0d060"
        }}>

          <Box sx={{
            position: "absolute",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            border: "40px solid rgba(202,138,4,.2)",
            bottom: "-100px",
            right: "-80px"
          }} />

          <Box sx={{ position: "relative", zIndex: 1 }}>

            <Typography sx={{
              ...styles.unbounded,
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 900,
              color: "#1c1400",
              lineHeight: 1.05,
              letterSpacing: "-0.04em",
              mb: 2
            }}>
              SIGN IN<br />TO YOUR<br />WORKSPACE.
            </Typography>

            <Typography sx={{
              color: "#3d2e00",
              fontSize: "0.9rem",
              lineHeight: 1.65,
              maxWidth: "320px",
              fontWeight: 300,
              mb: 5
            }}>
              Manage visitors, residents and facility operations
              securely from one centralized platform.
            </Typography>

            {/* STATS */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

              <Paper sx={{ p: 2, borderRadius: "10px" }}>
                {/* <Typography sx={{ fontSize: "0.7rem" }}>
                  VISITORS MANAGED
                </Typography> */}
                <Typography sx={{ fontWeight: 900 }}>
                  Visitor Management
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: "10px" }}>
                {/* <Typography sx={{ fontSize: "0.7rem" }}>
                  ACTIVE FACILITIES
                </Typography> */}
                <Typography sx={{ fontWeight: 900 }}>
                  Society Management
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: "10px" }}>
                {/* <Typography sx={{ fontSize: "0.7rem" }}>
                  SYSTEM UPTIME
                </Typography> */}
                <Typography sx={{ fontWeight: 900 }}>
                  Intelligent Operations
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: "10px" }}>
                {/* <Typography sx={{ fontSize: "0.7rem" }}>
                  SYSTEM UPTIME
                </Typography> */}
                <Typography sx={{ fontWeight: 900 }}>
                  Templates Management
                </Typography>
              </Paper>

            </Box>
          </Box>
        </Box>

        {/* RIGHT PANEL */}
        <Box sx={{
          backgroundColor: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: "40px 6vw", md: "80px 6vw" }
        }}>

          <Box sx={{ width: "100%", maxWidth: "390px" }}>

            <Typography sx={{
              ...styles.unbounded,
              fontSize: "1.6rem",
              fontWeight: 900,
              letterSpacing: "-0.04em",
              color: "#1c1400",
              mb: 1
            }}>
              WELCOME BACK.
            </Typography>

            <Typography sx={{
              color: "#8a7a40",
              fontSize: "0.85rem",
              mb: 3
            }}>
              Sign in with your google account to access your dashboard.
            </Typography>

            {/* RECAPTCHA */}
            <Box sx={{ mb: 3 }}>
              <ReCaptcha onVerify={handleCaptchaVerify} />
            </Box>

            {/* GOOGLE LOGIN */}
            <Button
              onClick={captchaVerified ? handleGoogleLogin : null}
              disabled={!captchaVerified || isLoading}
              sx={{
                width: "100%",
                p: "13px",
                borderRadius: "8px",
                border: "2px solid #f0d060",
                backgroundColor: "#fefce8",
                color: "#1c1400",
                mb: 3
              }}
            >
              <img
                width="24"
                height="24"
                src="https://img.icons8.com/color/48/google-logo.png"
                alt="google"
                style={{ marginRight: 10 }}
              />
              {isLoading ? <CircularProgress size={22} /> : "Continue with Google"}
            </Button>

            {/* <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 3
            }}>
              <Box sx={{ flex: 1, height: "2px", backgroundColor: "#f0d060" }} />
              <Typography sx={{ fontSize: "0.75rem", color: "#8a7a40" }}>
                or with email code
              </Typography>
              <Box sx={{ flex: 1, height: "2px", backgroundColor: "#f0d060" }} />
            </Box> */}

            {/* EMAIL LOGIN */}
            {/* <Paper sx={{
              p: 3,
              borderRadius: "10px",
              border: "2px solid #f0d060"
            }}>
              <Typography sx={{
                ...styles.unbounded,
                fontSize: "0.75rem",
                fontWeight: 700,
                textAlign: "center",
                mb: 2
              }}>
                LOGIN WITH OTP
              </Typography>

              <Input
                fullWidth
                placeholder="you@company.com"
                sx={{ mb: 2 }}
              />

              <Button
                fullWidth
                sx={{
                  backgroundColor: "#1c1400",
                  color: "#fde047"
                }}
              >
                SEND LOGIN CODE
              </Button>
            </Paper> */}

            <Typography sx={{ textAlign: "center", mt: 3 }}>
              New here?{" "}
              <span
                style={{ color: "#ca8a04", cursor: "pointer" }}
                onClick={() => navigate("/signup")}
              >
                Create your account
              </span>
            </Typography>

          </Box>
        </Box>

      </Box>

    </Box >
  );
}

export default LoginPage;
